import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Download, Shield, Zap, Sparkles, ArrowRight, Chrome, Puzzle, LockOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import GradientBackground from "@/components/GradientBackground";
import ParticleBackground from "@/components/ParticleBackground";

const ExtensionThankYou = () => {
    const [mounted, setMounted] = useState(false);
    const [showFeatures, setShowFeatures] = useState(false);
    const [showSteps, setShowSteps] = useState(false);

    useEffect(() => {
        setMounted(true);
        setTimeout(() => setShowFeatures(true), 500);
        setTimeout(() => setShowSteps(true), 1000);
    }, []);

    const features = [
        { icon: Shield, title: "Privacy Guard", desc: "Auto-mask sensitive data" },
        { icon: LockOpen, title: "Credential Vault", desc: "Secure password storage" },
        { icon: Zap, title: "PDF Copilot", desc: "AI-powered PDF analysis" },
        { icon: Sparkles, title: "Temp Mail", desc: "Disposable email addresses" },
    ];

    const steps = [
        { num: 1, title: "Link Your Account", desc: "Go to Dashboard → Extension and generate a linking code" },
        { num: 2, title: "Enter Code in Extension", desc: "Click the extension icon and enter your 6-digit code" },
        { num: 3, title: "Start Using!", desc: "All features are now synced with your TAM account" },
    ];

    return (
        <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-background via-background to-primary/5">
            <GradientBackground />
            <ParticleBackground />

            <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-16">
                {/* Success Icon with 3D Animation */}
                <div
                    className={`relative mb-8 transition-all duration-1000 ${mounted ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0'
                        }`}
                >
                    {/* Outer glow rings */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-40 h-40 rounded-full bg-gradient-to-r from-primary/20 to-purple-500/20 animate-ping" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-32 h-32 rounded-full bg-gradient-to-r from-primary/30 to-purple-500/30 animate-pulse" />
                    </div>

                    {/* 3D floating icon */}
                    <div
                        className="relative w-28 h-28 flex items-center justify-center rounded-full 
                       bg-gradient-to-br from-primary via-purple-500 to-primary
                       shadow-[0_0_60px_rgba(99,102,241,0.5),0_20px_40px_rgba(0,0,0,0.3)]
                       animate-bounce"
                        style={{
                            transform: 'perspective(1000px) rotateX(10deg)',
                            animation: 'float3d 3s ease-in-out infinite'
                        }}
                    >
                        <Check className="w-14 h-14 text-white drop-shadow-lg" />
                    </div>
                </div>

                {/* Main Content */}
                <div
                    className={`text-center mb-10 transition-all duration-700 delay-200 ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                        }`}
                >
                    <h1 className="text-4xl md:text-5xl font-black mb-4 gradient-text">
                        🎉 Installation Complete!
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-md mx-auto">
                        TAM Tools Extension is now ready to supercharge your browsing
                    </p>
                </div>

                {/* Features Grid */}
                <div
                    className={`grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mb-12 transition-all duration-700 ${showFeatures ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                        }`}
                >
                    {features.map((feature, i) => (
                        <div
                            key={i}
                            className="glass p-4 rounded-xl text-center border border-border/50 hover:border-primary/50 
                         transition-all duration-300 hover:-translate-y-2 hover:shadow-lg hover:shadow-primary/20"
                            style={{ animationDelay: `${i * 100}ms` }}
                        >
                            <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 
                            flex items-center justify-center">
                                <feature.icon className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="font-semibold text-sm mb-1">{feature.title}</h3>
                            <p className="text-xs text-muted-foreground">{feature.desc}</p>
                        </div>
                    ))}
                </div>

                {/* Getting Started Steps */}
                <div
                    className={`glass rounded-2xl p-8 max-w-2xl w-full border border-border/50 mb-8 transition-all duration-700 ${showSteps ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                        }`}
                >
                    <h2 className="text-2xl font-bold mb-6 text-center flex items-center justify-center gap-2">
                        <Puzzle className="w-6 h-6 text-primary" />
                        Get Started in 3 Easy Steps
                    </h2>

                    <div className="space-y-4">
                        {steps.map((step, i) => (
                            <div
                                key={i}
                                className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-primary/5 to-purple-500/5 
                         border border-border/30 hover:border-primary/30 transition-all"
                            >
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-500 
                              flex items-center justify-center text-white font-bold shrink-0
                              shadow-lg shadow-primary/30">
                                    {step.num}
                                </div>
                                <div>
                                    <h3 className="font-semibold mb-1">{step.title}</h3>
                                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA Buttons */}
                <div
                    className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-500 ${showSteps ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                        }`}
                >
                    <Link to="/dashboard/extension">
                        <Button size="lg" className="gap-2 px-8 shadow-lg shadow-primary/30">
                            <ArrowRight className="w-5 h-5" />
                            Link Your Account
                        </Button>
                    </Link>
                    <Link to="/dashboard">
                        <Button variant="outline" size="lg" className="gap-2 px-8">
                            Go to Dashboard
                        </Button>
                    </Link>
                </div>

                {/* Footer */}
                <p className="mt-12 text-sm text-muted-foreground">
                    Powered by <span className="text-primary font-semibold">TAM</span> • v1.0.0
                </p>
            </div>

            {/* Custom CSS for 3D animation */}
            <style>{`
        @keyframes float3d {
          0%, 100% {
            transform: perspective(1000px) rotateX(10deg) translateY(0);
          }
          50% {
            transform: perspective(1000px) rotateX(-5deg) translateY(-15px);
          }
        }
      `}</style>
        </div>
    );
};

export default ExtensionThankYou;
